import { getFromDataPath, isSubClauseWithReference, ReferencedEntityControl } from "aic";
import { useARSContext } from "../contexts";
import { isSubClause } from "../typeGuards/isSubClause";
import { isSubClauseWithCompoundExpression } from "../typeGuards/isSubClauseWithCompoundExpression";
import { isSubClauseWithCondition } from "../typeGuards/isSubClauseWithCondition";
import { WhereClauseConditionItemControl } from "./WhereClauseConditionItemControl";
import { WhereClauseEditor } from "./WhereClauseEditor";

/**
 * Props for the SubClauseItemControl component.
 */
export interface SubClauseItemControlProps {
  /** RFC 6901 JSON Pointer path to the SubClause in ARSContext state */
  dataPath: string;
  /** If this sub-clause is part of a compound expression and can be removed */
  isRemovable?: boolean;
}

/**
 * A control for rendering a single SubClause item.
 *
 * This component determines the type of sub-clause and renders the appropriate control:
 * - SubClauseWithCondition: Renders WhereClauseConditionItemControl
 * - SubClauseWithCompoundExpression: Renders nested WhereClauseEditor
 * - SubClauseWithReference: Renders ReferencedEntityControl for DataSubset entity type
 *
 * This enables recursive nesting of where clause expressions.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the SubClause in ARSContext state.
 * @param props.isRemovable - Indicates if this sub-clause can be removed.
 * @returns A React functional component rendering the appropriate sub-clause control.
 */
export const SubClauseItemControl = ({
  dataPath,
  isRemovable = false,
}: SubClauseItemControlProps): React.ReactElement => {
  const { state } = useARSContext();

  // Get the sub-clause from state
  const subClause = getFromDataPath(dataPath, state, isSubClause);

  return isSubClauseWithCompoundExpression(subClause) ? (
    <div style={{ paddingLeft: "1rem", borderLeft: "2px solid #dee2e6" }}>
      <WhereClauseEditor dataPath={dataPath} />
    </div>
  ) : isSubClauseWithCondition(subClause) ? (
    <WhereClauseConditionItemControl
      dataPath={`${dataPath}/condition`}
      isRemovable={isRemovable}
    />
  ) : isSubClauseWithReference(subClause) ? (
    <div>
      <ReferencedEntityControl
        dataPath={`${dataPath}/subClauseId`}
        entityType="DataSubset"
      />
    </div>
  ) : (
    <span className="text-danger">SubClause not found at path: {dataPath}</span>
  );
};

SubClauseItemControl.displayName = "SubClauseItemControl";
